import React from 'react';
import { Table, type TableColumnsType, type TableProps } from 'antd';

import { CustomTooltip } from '~components/Tooltip';
import { dates } from '~library/dates';
import {
  STATUS_CODE_CLASS_LABELS,
  STATUS_CODE_CLASSES,
  selectedStatusCodeClasses,
  type StatusCodeClass,
} from '../StatusCodeClass';

import { formatCtr, formatInspectionState, formatMetricCount, type PagesMetricState } from './PagesFormatting';
import PagesHttpStatusValue from './PagesHttpStatusValue';
import PagesLastCrawledValue from './PagesLastCrawledValue';
import { PAGES_PAGE_SIZE } from './PagesPaging';
import { type PagesSort, type PagesSortField } from './PagesSort';
import PagesUrlCell, { URL_ACTIONS_CSS } from './PagesUrlCell';

export type PagesRow = {
  key: string;
  pageId: string;
  url: string;
  impressions: number | null;
  clicks: number | null;
  ctrPct: number | null;
  /** Latest in-window crawler-served status. `null` renders `—`. */
  lastStatusCode: number | null;
  /** Latest Googlebot request in the window. `null` renders `—`. */
  lastCrawledAt: string | null;
  metricState: PagesMetricState;
  coverageState: string | null;
  inspectedAt: string | null;
};

export type PagesTableChange = {
  /** Which interaction produced this change — lets the caller track sort vs filter apart. */
  action: 'sort' | 'filter' | 'paginate';
  /** 1-based page after the interaction. Sort and filter changes always land on page 1. */
  page: number;
  /** `null` is antd's third-click "cancel sort" — the caller applies the tab's default. */
  sort: PagesSort | null;
  /** Status classes the customer checked; `undefined` when no class is selected. */
  statusFilter: StatusCodeClass[] | undefined;
};

const PAGES_TABLE_STRINGS = {
  columns: {
    pageUrl: 'Page URL',
    impressions: 'Impressions',
    clicks: 'Clicks',
    ctrPercent: 'CTR%',
    httpStatus: 'HTTP Status',
    lastCrawled: 'Last crawled',
    inspectionStatus: 'Index status',
  },
  formatLastInspected: (inspectedAt: string) => `Prerender inspected this URL on ${dates.pointInTime(inspectedAt)}.`,
  formatTotal: (total: number) => `${total.toLocaleString()} pages`,
};

export type PagesTableProps = {
  rows: PagesRow[];
  loading: boolean;
  /** 1-based current page of the server-side pagination. */
  page: number;
  /** Unpaged row count. */
  total: number;
  sort: PagesSort;
  /** Columns that may order this tab; the others keep their header but no sorter. */
  sortableFields: readonly PagesSortField[];
  /** Header tooltip on the columns outside `sortableFields`. */
  unsortableReason: string;
  statusFilter: StatusCodeClass[] | undefined;
  emptyText: React.ReactNode;
  onChange: (change: PagesTableChange) => void;
  /** Called when the customer selects a row. Omitted hides the row action. */
  onInspectRow?: (row: PagesRow) => void;
  /** Called when a row URL is opened in a new tab (the link or the export icon). */
  onUrlClick?: (url: string) => void;
  showInspection?: boolean;
};

/** Controlled: every sortable column needs `sorter: true` and explicit `sortOrder` (`null` on inactive columns, or antd runs them uncontrolled). */
function PagesTable({
  rows,
  loading,
  page,
  total,
  sort,
  sortableFields,
  unsortableReason,
  statusFilter,
  emptyText,
  onChange,
  onInspectRow,
  onUrlClick,
  showInspection = false,
}: PagesTableProps) {
  const sortColumn = (field: PagesSortField, label: string) => {
    if (!sortableFields.includes(field)) {
      return {
        title: (
          <CustomTooltip placement="top" message={unsortableReason}>
            <span>{label}</span>
          </CustomTooltip>
        ),
        sorter: false as const,
        sortOrder: null,
      };
    }
    // Descending first: every tab's default sort is descending, and antd's own
    // cycle (ascend → descend → cancel) would step a default-sorted column
    // straight to "cancel" — which the caller maps back to that same default,
    // so the header could never be re-sorted.
    return {
      title: label,
      sorter: true as const,
      sortDirections: ['descend' as const, 'ascend' as const],
      sortOrder: sort.field === field ? (sort.order === 'asc' ? ('ascend' as const) : ('descend' as const)) : null,
    };
  };

  const columns: TableColumnsType<PagesRow> = [
    {
      title: PAGES_TABLE_STRINGS.columns.pageUrl,
      dataIndex: 'url',
      key: 'url',
      render: (url: string, record) => (
        <PagesUrlCell
          url={url}
          onInspect={onInspectRow ? () => onInspectRow(record) : undefined}
          onUrlClick={onUrlClick}
        />
      ),
    },
    {
      ...sortColumn('impressions', PAGES_TABLE_STRINGS.columns.impressions),
      dataIndex: 'impressions',
      key: 'impressions',
      align: 'right',
      width: 134,
      render: (value: number | null, record) => formatMetricCount(value, record.metricState),
    },
    {
      ...sortColumn('clicks', PAGES_TABLE_STRINGS.columns.clicks),
      dataIndex: 'clicks',
      key: 'clicks',
      align: 'right',
      width: 92,
      render: (value: number | null, record) => formatMetricCount(value, record.metricState),
    },
    {
      ...sortColumn('ctrPct', PAGES_TABLE_STRINGS.columns.ctrPercent),
      dataIndex: 'ctrPct',
      key: 'ctrPct',
      align: 'right',
      width: 102,
      render: (value: number | null) => formatCtr(value),
    },
    ...(showInspection
      ? [
          {
            title: PAGES_TABLE_STRINGS.columns.inspectionStatus,
            dataIndex: 'coverageState',
            key: 'coverageState',
            // Google's wording is verbatim and long ("Duplicate, Google chose different canonical than user"): wrap, don't widen.
            width: 160,
            render: (_: string | null, record: PagesRow) => (
              <CustomTooltip
                placement="top"
                message={record.inspectedAt ? PAGES_TABLE_STRINGS.formatLastInspected(record.inspectedAt) : undefined}
              >
                {formatInspectionState(record)}
              </CustomTooltip>
            ),
          },
        ]
      : []),
    {
      title: PAGES_TABLE_STRINGS.columns.httpStatus,
      dataIndex: 'lastStatusCode',
      key: 'lastStatusCode',
      align: 'center',
      width: 136,
      filters: STATUS_CODE_CLASSES.map((cls) => ({ text: STATUS_CODE_CLASS_LABELS[cls], value: cls })),
      filterMultiple: true,
      filteredValue: statusFilter && statusFilter.length > 0 ? statusFilter : null,
      // No `onFilter`: server-side; this column only reports the selection through `onChange`.
      render: (statusCode: number | null, record) => (
        <PagesHttpStatusValue statusCode={statusCode} lastCrawledAt={record.lastCrawledAt} />
      ),
    },
    {
      ...sortColumn('lastCrawledAt', PAGES_TABLE_STRINGS.columns.lastCrawled),
      dataIndex: 'lastCrawledAt',
      key: 'lastCrawledAt',
      // Wider than the other narrow columns: "21 Aug 2026, 17:56" wraps to two lines at 136.
      width: 168,
      render: (lastCrawledAt: string | null, record) => (
        // nowrap stays on the column: the drawer has no width constraint to defend.
        <span style={{ whiteSpace: 'nowrap' }}>
          <PagesLastCrawledValue lastCrawledAt={lastCrawledAt} lastStatusCode={record.lastStatusCode} />
        </span>
      ),
    },
  ];

  const handleChange: TableProps<PagesRow>['onChange'] = (pagination, filters, sorter, extra) => {
    const nextStatusFilter = selectedStatusCodeClasses(filters.lastStatusCode);

    if (extra.action === 'sort') {
      const single = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextSort = single?.order
        ? {
            field: single.field as PagesSortField,
            order: single.order === 'ascend' ? ('asc' as const) : ('desc' as const),
          }
        : null;
      // antd does not reset a controlled `current` on sort — a new order
      // starts from page 1 or the caller keeps reading a stale offset.
      onChange({ action: 'sort', page: 1, sort: nextSort, statusFilter: nextStatusFilter });
      return;
    }
    if (extra.action === 'filter') {
      // Same stale-offset hazard as sorting.
      onChange({ action: 'filter', page: 1, sort, statusFilter: nextStatusFilter });
      return;
    }
    if (extra.action === 'paginate') {
      onChange({ action: 'paginate', page: pagination.current ?? 1, sort, statusFilter: nextStatusFilter });
    }
  };

  return (
    <>
      <style>{URL_ACTIONS_CSS}</style>
      <Table<PagesRow>
        dataSource={rows}
        columns={columns}
        rowKey="key"
        loading={loading}
        onChange={handleChange}
        pagination={{
          current: page,
          pageSize: PAGES_PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: PAGES_TABLE_STRINGS.formatTotal,
        }}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{ emptyText }}
      />
    </>
  );
}

export default PagesTable;
